<template>
  <div class="staff-telemetry-overlay" @click.self="$emit('close')">
    <div
      ref="dialogEl"
      class="staff-telemetry-popup"
      role="dialog"
      aria-label="Telemetry"
    >
      <div class="staff-telemetry-header">
        <span>Telemetry</span>
        <button type="button" aria-label="Close" @click="$emit('close')">
          <i class="fas fa-times"></i>
        </button>
      </div>
      <div class="staff-telemetry-body">
        <p v-if="loading">Loading…</p>
        <p v-else-if="error">{{ error }}</p>
        <template v-else>
          <h3>Notifications, last 7 days</h3>
          <p v-if="!notifications.length" class="staff-telemetry-empty">
            No notifications sent in the last 7 days.
          </p>
          <table v-else class="staff-telemetry-table">
            <thead>
              <tr>
                <th>User</th>
                <th v-for="type in notificationTypes" :key="type">{{ type }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in notifications" :key="row.userId">
                <td>{{ row.name }}</td>
                <td v-for="type in notificationTypes" :key="type">
                  {{ row.counts[type] || 0 }}
                </td>
              </tr>
            </tbody>
          </table>

          <h3>Texts from the Twilio number</h3>
          <p v-if="!textRows.length" class="staff-telemetry-empty">
            No texts in the last 7 days.
          </p>
          <table v-else class="staff-telemetry-table">
            <thead>
              <tr>
                <th>Staff</th>
                <th v-for="day in textDays" :key="day.day">{{ day.label }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in textRows" :key="row.userId || row.name">
                <td>{{ row.name }}</td>
                <td v-for="day in textDays" :key="day.day">
                  {{ row.counts[day.day] || 0 }}
                </td>
              </tr>
            </tbody>
          </table>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/axiosConfig';
import { makeDraggable } from '@/utils/makeDraggable';

export default {
  name: 'StaffTelemetryPopup',
  emits: ['close'],
  data() {
    return {
      loading: true,
      error: '',
      notificationTypes: [],
      notifications: [],
      textDays: [],
      textRows: [],
      dragCleanup: null,
    };
  },
  mounted() {
    this.$nextTick(() => this.initDrag());
    this.loadTelemetry();
  },
  beforeUnmount() {
    if (this.dragCleanup) this.dragCleanup();
  },
  methods: {
    initDrag() {
      const el = this.$refs.dialogEl;
      if (!el || this.dragCleanup) return;
      const hasSaved =
        localStorage.getItem('staffTelemetryPopupTop') &&
        localStorage.getItem('staffTelemetryPopupLeft');
      if (!hasSaved) {
        const width = el.offsetWidth || 760;
        const height = el.offsetHeight || 420;
        el.style.top = `${Math.max(16, (window.innerHeight - height) / 2)}px`;
        el.style.left = `${Math.max(16, (window.innerWidth - width) / 2)}px`;
      }
      this.dragCleanup = makeDraggable(el, {
        handleSelector: '.staff-telemetry-header',
        storageKeyPrefix: 'staffTelemetryPopup',
      });
    },
    async loadTelemetry() {
      try {
        const response = await axios.get('/api/staff-telemetry');
        const data = response.data || {};
        this.notificationTypes = data.notificationTypes || [];
        this.notifications = data.notifications || [];
        const texts = data.texts || {};
        this.textDays = texts.days || [];
        this.textRows = texts.rows || [];
      } catch (err) {
        console.error('Error loading staff telemetry:', err);
        this.error = 'Could not load telemetry.';
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.staff-telemetry-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 99999;
}

.staff-telemetry-popup {
  position: fixed;
  width: min(760px, calc(100vw - 32px));
  background: #fff;
  color: #333;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
}

.staff-telemetry-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: #6e5875;
  color: #fff;
  border-radius: 8px 8px 0 0;
  font-weight: 600;
  cursor: move;
}

.staff-telemetry-header button {
  border: none;
  background: transparent;
  color: #fff;
  cursor: pointer;
  font-size: 14px;
}

.staff-telemetry-body {
  padding: 12px;
  max-height: min(70vh, 520px);
  overflow: auto;
  font-size: 13px;
}

.staff-telemetry-body h3 {
  margin: 0 0 8px;
  font-size: 14px;
  color: #2c3e50;
}

.staff-telemetry-body h3 + h3,
.staff-telemetry-table + h3 {
  margin-top: 16px;
}

.staff-telemetry-empty {
  margin: 0 0 16px;
  color: #666;
}

.staff-telemetry-table {
  width: 100%;
  border-collapse: collapse;
}

.staff-telemetry-table th,
.staff-telemetry-table td {
  border: 1px solid #e6e0ea;
  padding: 6px 8px;
  text-align: left;
}

.staff-telemetry-table th {
  background: #f6f1f8;
  font-weight: 600;
}

.staff-telemetry-table td:not(:first-child),
.staff-telemetry-table th:not(:first-child) {
  text-align: right;
}
</style>
